import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useReturnHomeOnReshow } from "../../hooks/useReturnHomeOnReshow";
import { deriveRoute } from "../../routes";
import { SingleAssetAgentDev } from "../single-asset-agent-dev/SingleAssetAgentDev";
import { AssetOverview } from "../asset-overview/AssetOverview";
import { ScreenerPage } from "../screener/ScreenerPage";
import { TagSummaryPage } from "../tag-summary/TagSummaryPage";
import { MarketObservationApp } from "./MarketObservation";


export function MarketRouteBoundary({ fallback, session, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const route = React.useMemo(() => deriveRoute(location), [location]);
  useReturnHomeOnReshow(navigate);  // 在 Lucimix 里切走再切回 → 回概览
  // 从选股页 / 标签页进来：返回 = 跳回 returnTo，带 restore 让选股页恢复搜索结果。
  // 不能用浏览器后退（history.back）：嵌在 Lucimix 里时浏览器历史混着 Lucimix 切应用的记录，后退会退出本应用（或被 iframe 沙箱拦成没反应）。
  const returnTo = location.state?.returnTo;
  React.useEffect(() => {
    if (route.redirectTo) navigate(route.redirectTo, { replace: true });
  }, [route.redirectTo, navigate]);
  return (
    <>
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      {route.mode === "single_asset_agent"
        ? <SingleAssetAgentDev
            initialConversationId={route.singleConversationId}
            initialBindingId={route.singleBindingId}
            initialAssetQuery={route.singleAssetQuery}
            initialHoldingId={route.singleHoldingId}
            initialWorkspaceId={route.singleWorkspaceId}
            initialAgentHandoff={location.state?.singleAssetAgentHandoff}
            initialMarketView={location.state?.singleAssetMarketView}
            initialBindingView={location.state?.singleAssetBindingView}
            initialOpenHoldingEntry={location.state?.singleAssetOpenHoldingEntry}
            onBack={(agentHandoff) => returnTo ? navigate(returnTo, { state: { restore: true } }) : navigate("/overview", {
              state: { singleAssetAgentHandoff: agentHandoff },
            })}
            onLogout={onLogout}
            onScreenerOpen={(agentQuery) => navigate("/screener", { state: { agentQuery } })}
            onEmptyWorkspace={() => navigate("/overview", { replace: true })}
            onSessionRouteChange={({ conversationId, bindingId, viewMode, assetCode, targetId }) => {
              const query = new URLSearchParams();
              query.set("conversation", conversationId);
              if (bindingId) query.set("binding", bindingId);
              if (viewMode === "market" && assetCode) query.set("asset", assetCode);
              if (viewMode === "holding" && targetId) query.set("holding", targetId);
              if (viewMode === "workspace" && targetId) query.set("workspace", targetId);
              navigate(`/single?${query.toString()}`, { replace: true, state: returnTo ? { returnTo } : undefined });
            }}
          />
        : route.mode === "asset_overview"
          ? <AssetOverview
              navigate={navigate}
              onLogout={onLogout}
              initialAgentHandoff={location.state?.singleAssetAgentHandoff}
            />
        : route.mode === "screener"
          ? <ScreenerPage navigate={navigate} onLogout={onLogout} onBack={() => navigate("/overview")} />
        : route.mode === "tag_summary"
          ? <TagSummaryPage tagName={route.tagName} navigate={navigate} onLogout={onLogout} onBack={() => navigate("/overview")} />
        : route.mode === "market"
          ? <MarketObservationApp route={route} navigate={navigate} />
          : fallback}
    </>
  );
}
